// GENERATED from funviz-amplify/resources/screener-compute/lib/scoreCore.mjs by scripts/sync-score-core.sh — DO NOT EDIT
// body-sha256: 368e8533f1d2e1191135b8fe089015b43f0b01b596a1ab2c86a216b931490833
// resources/screener-compute/lib/scoreCore.mjs
//
// CANONICAL Quality / Growth score core. Shared by two engines:
//   - the screener: lib/houseScores.mjs (buildLatestHouseScores), and
//   - the Analysis page: funviz-react src/components/FunViz/utils/scoreCore.js,
//     a GENERATED copy written by funviz-amplify scripts/sync-score-core.sh.
//     Do NOT edit that copy; edit this file and re-run the sync script.
//
// Holds the constants, every pillar and rule function, the raw-row builder
// (buildQualityRows), the per-fiscal-year Quality engine and the strict
// 3/5/10-year Quality headline. The Growth per-year RULE helpers (peak, trend,
// score, lender gate) live here too; each engine feeds them its own inputs.
//
// Zero imports, zero network, plain ESM. The React copy is byte-identical
// except for a generated header, and a hash guard in funviz-react fails if it
// is hand-edited. Cross-repo net: test/fixtures/quality-parity/golden.json.

// ─── small numeric helpers (verbatim from qualityScore.js) ────────────────

export const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);

export const clamp = (value, lo, hi) => Math.min(hi, Math.max(lo, value));

export const mean = (values) => values.reduce((sum, value) => sum + value, 0) / values.length;

export function round(x, dp) {
  if (!Number.isFinite(x)) return x;
  const f = 10 ** dp;
  return Math.round(x * f) / f;
}

export function isPosFinite(x) {
  return Number.isFinite(x) && x > 0;
}

/** Population standard deviation. Identical values -> 0, the ceiling case. */
export const populationStdev = (values) => {
  const center = mean(values);
  const variance = values.reduce((sum, value) => sum + (value - center) ** 2, 0) / values.length;
  return Math.sqrt(variance);
};

// ─── Quality-score pillar caps — verbatim from qualityScore.js ────────────
// "The old calibration — do not refit them." (same comment, same source).
export const QUALITY_CAPS = {
  window: 3,
  profitability: {
    max: 0.30,
    clip: 0.15,
    scale: 2.0,
    assetTurnoverDivisor: 10,
  },
  dilution: {
    floor: -0.30,
    saturation: 0.30,
  },
  leverage: {
    max: 0.30,
  },
  fcf: {
    // Generic path 0.25 (no stability terms); financial equity/assets path
    // keeps the pre-2026-11-03 pillar: stability terms, max 0.40.
    max: 0.25,
    financialMax: 0.40,
    growthClip: 0.08,
    growthScale: 1.25,
    growthWeight: 1.5,
    earningsClip: 0.10,
    growthCvCeiling: 1.4,
    levelCvCeiling: 10,
  },
  revenue: {
    // Generic path 0.15 (no stability terms); financial path 0.30 with them.
    max: 0.15,
    financialMax: 0.30,
    growthWeight: 1.5,
    growthCvCeiling: 2.2,
    levelCvCeiling: 15,
  },
  stability: {
    perfect: 0.10,
    weight: 0.75,
  },
  // Consistency pillar (2026-11-03), generic path only: EPS direction over
  // 6 consecutive years (5 YoY changes) and operating-margin stability over
  // 5, mean of the two in [-1, 1], times max. Range [-0.40, 0.40].
  consistency: {
    max: 0.40,
    epsYears: 6,
    marginYears: 5,
    marginInverseCvZero: 2,
    marginInverseCvFull: 10,
    epsWeight: 0.5,
    marginWeight: 0.5,
  },
  quality: {
    min: 0,
    max: 1,
  },
};

/**
 * Inverse CV (`mean / std`), clipped to +-`ceiling`, then scaled so a reading
 * at the ceiling contributes `stability.perfect` (0.10) before the 0.75
 * weight. Verbatim from qualityScore.js's stabilityTerm.
 */
export const stabilityTerm = (values, ceiling) => {
  if (!values || values.length < QUALITY_CAPS.window || !(ceiling > 0)) return null;
  if (!values.every(isFiniteNumber)) return null;

  const center = mean(values);
  const identical = values.every((value) => value === values[0]);
  const std = populationStdev(values);
  let inverseCv;
  if (identical || std === 0) {
    if (center > 0) inverseCv = ceiling;
    else if (center < 0) inverseCv = -ceiling;
    else inverseCv = 0;
  } else {
    inverseCv = center / std;
  }
  if (!isFiniteNumber(inverseCv)) return null;

  const { perfect, weight } = QUALITY_CAPS.stability;
  const clipped = clamp(inverseCv, -ceiling, ceiling);
  return (perfect * clipped / ceiling) * weight;
};

export const finiteWindow = (values) => (
  Array.isArray(values)
  && values.length === QUALITY_CAPS.window
  && values.every(isFiniteNumber)
);

/**
 * Profitability, max 0.30. Verbatim from qualityScore.js.
 * `mean(netMargin, operatingRoa) / 2 + assetTurnover / 10`, clip to
 * [0, 0.15], x 2.0. Missing terms are omitted from the mean. `operatingRoa`
 * is operatingIncome / totalAssets (screener only; the Analysis page has no
 * balance sheet and scores on net margin alone).
 */
export const profitabilityPillar = ({
  netMargin = null, operatingRoa = null, assetTurnover = null,
} = {}) => {
  const ratios = [netMargin, operatingRoa].filter(isFiniteNumber);
  const hasTurnover = isFiniteNumber(assetTurnover);
  if (!ratios.length && !hasTurnover) return null;
  let raw = ratios.length ? mean(ratios) / 2 : 0;
  if (hasTurnover) raw += assetTurnover / QUALITY_CAPS.profitability.assetTurnoverDivisor;
  const { clip, scale } = QUALITY_CAPS.profitability;
  return clamp(raw, 0, clip) * scale;
};

/** Dilution, down to -0.30. Verbatim from qualityScore.js. */
export const dilutionPillar = (sharesNow, sharesPrior) => {
  if (!isFiniteNumber(sharesNow) || !isFiniteNumber(sharesPrior) || sharesPrior === 0) return null;
  const increase = (sharesNow - sharesPrior) / Math.abs(sharesPrior);
  if (!(increase > 0)) return 0;
  return -Math.min(increase, QUALITY_CAPS.dilution.saturation);
};

/**
 * Leverage, max 0.30 for the OPERATING-COMPANY case. Verbatim from
 * qualityScore.js's pre-2026-11-01 `leveragePillar` — this is now an
 * internal helper (`operatingLeverageCoverage`), not the exported dispatch
 * function; see the 2026-11-01 sector-leverage block below for why.
 *
 * FCF coverage (net debt / FCF) measures debt-service capacity against the
 * cash that actually services debt — the primary justification
 * (SCORE_CARDS.md §1.6/Q3). Its concave, 5x-capped shape matches the legacy
 * formula's Debt/FCF pillar (data_refinery.py), which corroborates that the
 * shape is reasonable but is not proof the exact constants (`3`, `+5`, the
 * 5x cap) are optimal — they have not been independently retuned against
 * this codebase's own data. Keeps NET DEBT — never legacy's book-debt-
 * via-D/E — as the metric. Takes no price input: company value dropped
 * out entirely.
 *
 * Continuous at netDebt = 0 under either FCF sign: the coverage branch
 * evaluates to the same 0.30 (fcf > 0) / 0 (fcf <= 0) the early-return branch
 * gives.
 *
 * KNOWN CLIFF at fcf = 0 when netDebt <= 0. At fcf = 0 exactly both paths do
 * return 0, but the net-cash branch is a STEP, not a limit: any positive FCF
 * however small returns the full 0.30, so operatingLeverageCoverage(-10, 0)
 * is 0 while operatingLeverageCoverage(-10, 1e-9) is 0.30 — a 30-point jump
 * across an infinitesimal change. (The netDebt > 0 seam IS continuous there:
 * FCF -> 0+ sends coverage to the 5x cap, i.e. to 0.) The cliff is
 * deliberately left in place pending a design decision; it is documented,
 * not denied, and the test named for it asserts the step rather than
 * claiming continuity.
 *
 * CONSTANT COUPLING: the `3`, the `+ 5` and `QUALITY_CAPS.leverage.max` are
 * tuned together, and `leverage.max` is overloaded here as the curve's OFFSET
 * rather than as a cap. The curve spans exactly [0, max] only because
 * 3 / (0 + 5) == 2 x 0.30 and 3 / (5 + 5) == 0.30. Retuning `leverage.max`
 * alone breaks it in both directions — at max = 0.40 the netDebt > 0 branch
 * would peak at 0.20 (below the new cap) and bottom out at -0.10 (below zero)
 * while the net-cash early-return above would still hand back 0.40. Any change
 * to `leverage.max` must re-derive the numerator and the coverage-cap offset
 * with it. A separately named offset constant would NOT decouple this: all
 * three numbers move together.
 */
export function operatingLeverageCoverage(netDebt, fcf) {
  if (!isFiniteNumber(netDebt) || !isFiniteNumber(fcf)) return null;
  if (netDebt <= 0) return fcf > 0 ? QUALITY_CAPS.leverage.max : 0;
  const coverage = fcf > 0 ? Math.min(netDebt / fcf, 5) : 5;
  return 3 / (coverage + 5) - QUALITY_CAPS.leverage.max;
}

// ─── Sector-specific leverage (2026-11-01) ────────────────────────────────
//
// Until this change, `leveragePillar` returned `null` for every REIT and
// every "Financial Services"/"Financials" symbol, because net-debt/FCF
// coverage is not a meaningful number for either: a bank's FCF swings on
// loan-book growth, not distress, and a REIT's leverage is conventionally
// read off FFO/AFFO coverage, not FCF. `null` meant these pillars were
// OMITTED from `qualityFromPillars`' sum (never counted as 0), which biased
// every REIT/financial's Quality score downward relative to an operating
// company that has all 5 pillars available, and made the two incomparable.
//
// Simulated against 732 live REIT + financial symbols (EPCC_PLAN.md,
// 2026-11-01): a sector-appropriate balance-sheet ratio, run through the
// SAME linear-ramp shape as the rest of this file's pillars (full 0.30 at a
// "good" threshold, 0 at a "bad" threshold, linear between, clamped outside
// — never negative, matching this pillar's existing [0, 0.30] range), gives
// each sector a real leverage reading instead of a hole in the sum:
//
//   Sector                        Metric                 Good -> Bad (0.30 -> 0)
//   ---------------------------------------------------------------------------
//   REIT (`isReit`)                net debt / EBITDA*     3.5x  -> 7x   (2026-11-03; was 4x -> 9x)
//   Bank ("Banks - *")              equity / totalAssets   15%   -> 10.5%
//   Insurance (non-life underwriters) equity / totalAssets 68.5% -> 21%
//   Life insurance                 equity / totalAssets   30.5% -> 4%
//   Other financial (catch-all)    equity / totalAssets   85%   -> 19.5%
//
//   * REIT EBITDA = 3-year mean of min(FMP ebitda, operatingIncome + D&A),
//     see `reitLeverageEbitda`. A non-positive EBITDA scores 0.
//
//   The bank/insurance/life/other-financial thresholds shown here are the
//   2026-11-02 data-rebased, strict values (see below), not the original
//   2026-11-01 textbook anchors.
//
// 2026-11-02 REBASE (accepted by the owner). The 2026-11-01
// thresholds were TEXTBOOK anchors (Basel minimums, statutory-capital
// norms, rating-agency ranges), never checked against this codebase's own
// data. Checked on the full live universe (2026-11-02 production snapshot,
// raw/fundamentals re-run through this engine; 2,558 scored symbols, the
// engine reproduces the snapshot's qualityScore5y on all but 1), they let
// the sector path hand out the full 0.30 far more easily than the generic
// net-debt/FCF curve does.
//
// OWNER RULES: the financial special path must never make the full 0.30,
// or a high Quality score, easier to reach than the generic path, and it is
// STRICT BY OWNER DIRECTIVE: a bucket may max out at most about half as
// often as the generic curve (16.4% of scored names), and its share at 0 may
// rise up to, but not above, the generic curve's (54.8%) — the no-collapse
// limit that keeps the rejected "drop leverage for financials" design out.
// So "good" is the ~95th and "bad" the ~33rd percentile of each bucket's
// SCORED live latest-FY equity/totalAssets (shell companies are never
// scored). Equity/assets is deliberately NOT mapped onto the generic curve:
// there is no defensible mapping between the two.
//
//   Bucket (scored n)   p25    p33    p50    p95    good / bad   max-out  at 0
//   Generic curve (1,926)                                        16.4%  54.8%
//   Bank (225)         10.1%  10.6%  11.3%  15.0%  15%   / 10.5%   5.3%  31.1%
//   Insurance (59)     16.5%  21.1%  24.7%  68.6%  68.5% / 21%     5.1%  30.5%
//   Life (16, low n)    3.6%   4.2%   4.5%  30.6%  30.5% / 4%      6.2%  31.2%
//   Other fin (114)    14.2%  19.4%  38.4%  73.2%  85%   / 19.5%   6.8%  32.2%
//   (other fin: implausible rows excluded — equity <= 0, total assets moving
//   >10x year over year — before taking percentiles. Its cleaned p95 (73%)
//   still let 10.9% of scored years max out, against the generic 14.6%,
//   because crypto miners/treasuries (RIOT, CLSK, MARA) and holding
//   companies (AB) run 80-100% equity/assets legitimately; 85% is the
//   lowest "good" that keeps the max-out rate at half the generic's.)
//   (textbook bands' max-out: bank 36.0%, insurance 30.5%, life 31.2%,
//   other 76.5%; REIT, unchanged, 8.7%)
//
// otherFin no longer shares bank's band: its population is bimodal (asset
// managers/BDCs at 37-76% equity/assets by design; APO, KKR, SCHW, AXP, BX at
// 5-18%, inside the bank range), so a bank-style band maxed nearly all of it.
//
// SINGLE YEAR, NOT 3-YEAR-SMOOTHED — the opposite of the REIT EBITDA, whose
// smoothing cancels one-off sale gains. Equity/assets has no such noise, and
// smoothing hides real capital events: NYCB's 2023 trough (7.34%) reads 8.26%
// on a 3-year mean, ZION's 2022 trough (5.46%) reads 7.16%.
//
// The same years also swap their profitability, FCF-quality and
// revenue-quality inputs (net income / ROA), see the block after
// `leveragePillar`. Residuals (OPEN_ITEMS.md, ADR 0011 amendment):
// money-center banks (JPM, BAC, WFC, C: 8-9% equity/assets) score 0 here;
// their CET1 capital is not in FMP data. Equity/assets cannot see loan
// concentration (OZK still maxes) or equity quality (ROOT's SPAC capital).
//
// GOODWILL RULE (other-financial only): acquisitive, goodwill-heavy
// companies in the catch-all bucket (payment processors, insurance-broker
// roll-ups) report "equity" that is mostly acquisition premium, not
// tangible loss-absorbing capital — equity/assets overstates their real
// solvency cushion. When `goodwillAndIntangibleAssets > 0.5 * equity`,
// `otherFinLeverageValue` switches to the SAME net-debt/FCF coverage curve
// operating companies use (`operatingLeverageCoverage`) instead of
// equity/assets. Live-verified examples (2026-11-01 production snapshot):
// GPN's goodwill is 93% of equity, MA's is 195% of equity, BWIN's is 416% of
// equity — all three switch to FCF coverage. Non-positive equity with any
// goodwill counts as over the cutoff (MSCI, GSHD FY2025). The 50% cutoff is itself an
// exploratory threshold (EPCC_PLAN.md flags it for future fitting), not
// data-fitted; ~1/3 of the catch-all bucket was observed to cross it in the
// 732-symbol simulation.
//
// Every helper below returns a value in `[0, QUALITY_CAPS.leverage.max]`
// (never negative — same range the operating-company formula already has)
// or `null` when its required inputs are missing/non-finite, so it degrades
// exactly like every other pillar in this file: a `null` leverage pillar is
// OMITTED from the quality sum, never coerced to 0.

export const SECTOR_LEVERAGE_THRESHOLDS = {
  // net debt / EBITDA (×) — lower is better.
  // 2026-11-03 (ADR 0011 amendment): was 4x -> 9x. Strict by owner directive:
  // REIT-specific bands may max out at most ~half as often as the generic
  // curve and may sit at 0 no more often than it does. "Good" 3.5x is the
  // ~6th percentile of scored REIT-years' 3-year-smoothed net debt / EBITDA
  // (max-out 5.2% vs generic 12.8%); "bad" 7x is where the REIT zero share
  // (54.6%) reaches the generic zero share (55.2%).
  reit: { good: 3.5, bad: 7 },
  // equity / totalAssets (fraction) — higher is better. 2026-11-02 data
  // rebase (see the "Sector-specific leverage" block above): bank and
  // otherFin no longer share a threshold, and all four equity/assets
  // bands moved off their original textbook anchors onto real population
  // percentiles: good ~p95, bad ~p33 of each bucket's scored live
  // population (strict by owner directive, 2026-11-02 — see the rebase
  // note). Single fiscal year, never smoothed.
  bank: { good: 0.15, bad: 0.105 },
  insurance: { good: 0.685, bad: 0.21 },
  lifeInsurance: { good: 0.305, bad: 0.04 },
  otherFin: { good: 0.85, bad: 0.195 },
};

// goodwillAndIntangibleAssets / equity above this switches otherFinLeverageValue
// from equity/assets to net-debt/FCF coverage — see the goodwill-rule note above.
export const OTHER_FIN_GOODWILL_EQUITY_RATIO = 0.5;

/**
 * The goodwill override's test: goodwill + intangibles above
 * `OTHER_FIN_GOODWILL_EQUITY_RATIO` of positive equity, or any positive
 * goodwill against non-positive equity. Missing (not 0) inputs never fire it.
 */
export function isGoodwillHeavy(goodwillAndIntangibleAssets, equity) {
  return isFiniteNumber(goodwillAndIntangibleAssets) && isFiniteNumber(equity)
    && (equity > 0
      ? goodwillAndIntangibleAssets / equity > OTHER_FIN_GOODWILL_EQUITY_RATIO
      : goodwillAndIntangibleAssets > 0);
}

/**
 * Linear ramp shared by every sector helper below: `max` at `goodAt`, `0` at
 * `badAt`, linear in between, clamped outside — the same shape every other
 * pillar in this file uses, generalized over EITHER ramp direction so a
 * single function serves both "lower is better" (REIT's net debt/EBITDA)
 * and "higher is better" (every equity/assets ratio) without duplicating the
 * clamp/interpolate logic per direction.
 */
export function leverageRamp(value, goodAt, badAt, max = QUALITY_CAPS.leverage.max) {
  if (!isFiniteNumber(value)) return null;
  if (goodAt >= badAt) {
    // Higher value is better (e.g. equity / totalAssets): good threshold is
    // the numerically larger one.
    if (value >= goodAt) return max;
    if (value <= badAt) return 0;
    return max * (value - badAt) / (goodAt - badAt);
  }
  // Lower value is better (e.g. net debt / EBITDA): good threshold is the
  // numerically smaller one.
  if (value <= goodAt) return max;
  if (value >= badAt) return 0;
  return max * (badAt - value) / (badAt - goodAt);
}

/**
 * REIT leverage: net debt / EBITDA, 3.5x (max) -> 7x (0). A net-cash REIT
 * (netDebt <= 0) falls below the 3.5x floor automatically — no special
 * net-cash branch needed, unlike the operating-company formula, since this
 * ramp has no FCF-sign dependency at all. A non-positive EBITDA scores 0
 * (no earnings to service any debt); `null` when either input is missing.
 * `ebitda` is `reitLeverageEbitda`'s smoothed figure, not FMP's raw field.
 */
export function reitLeverageValue(netDebt, ebitda) {
  if (!isFiniteNumber(netDebt) || !isFiniteNumber(ebitda)) return null;
  if (ebitda <= 0) return 0;
  const { good, bad } = SECTOR_LEVERAGE_THRESHOLDS.reit;
  return leverageRamp(netDebt / ebitda, good, bad);
}

// REIT leverage's EBITDA: the ONE place it is defined (2026-11-01). Per
// fiscal year, min(FMP `ebitda`, operatingIncome + depreciationAndAmortization)
// — FMP's `ebitda` includes one-off gains on property sales (VNO FY2025: a
// ~$1.0B sale gain reads 4.0x reported vs 9.4x on this basis); the min()
// drops those gains but keeps impairments and losses. It also leaves out
// equity-method/JV income, consistent with net debt leaving out unconsolidated
// JV debt. When only one of the two figures exists, that one is used. Then
// the mean over `QUALITY_LEVERAGE_FCF_WINDOW` (3) consecutive fiscal years
// ending at `i`, falling back to the single year — the same shape as the
// operating formula's 3-year mean FCF. ADR 0011.
//
// FMP reports exactly 0 for `ebitda` / `operatingIncome` when it has no figure
// (EFC FY2023-24 and MFA FY2023 ebitda 0 beside a positive operatingIncome;
// CIM FY2022-24 operatingIncome 0 beside a large ebitda), so an exact 0 is
// MISSING here, the same convention as `guardedProfitabilityMargin`.
// D&A 0 stays real: mortgage REITs genuinely carry none.
export const reitEbitdaOfYear = (row) => {
  const reported = isFiniteNumber(row.ebitda) && row.ebitda !== 0 ? row.ebitda : null;
  const operating = isFiniteNumber(row.operatingIncome) && row.operatingIncome !== 0
    && isFiniteNumber(row.depreciationAndAmortization)
    ? row.operatingIncome + row.depreciationAndAmortization
    : null;
  if (reported == null) return operating;
  if (operating == null) return reported;
  return Math.min(reported, operating);
};

export function reitLeverageEbitda(rows, i) {
  return consecutiveWindowMean(rows, i, QUALITY_LEVERAGE_FCF_WINDOW, reitEbitdaOfYear)
    ?? (rows[i] ? reitEbitdaOfYear(rows[i]) : null);
}

/**
 * Bank leverage: equity / totalAssets, 15% (max) -> 10.5% (0), single fiscal
 * year. 2026-11-02 strict rebase: ~p95 / ~p33 of the 225 scored live banks.
 */
export function bankLeverageValue(equity, totalAssets) {
  if (!isFiniteNumber(equity) || !isPosFinite(totalAssets)) return null;
  const { good, bad } = SECTOR_LEVERAGE_THRESHOLDS.bank;
  return leverageRamp(equity / totalAssets, good, bad);
}

/**
 * Non-life insurer leverage (P&C, Diversified, Specialty, Reinsurance):
 * equity / totalAssets, 68.5% (max) -> 21% (0), single fiscal year.
 * 2026-11-02 strict rebase: ~p95 / ~p33 of the 59 scored live insurers.
 */
export function insuranceLeverageValue(equity, totalAssets) {
  if (!isFiniteNumber(equity) || !isPosFinite(totalAssets)) return null;
  const { good, bad } = SECTOR_LEVERAGE_THRESHOLDS.insurance;
  return leverageRamp(equity / totalAssets, good, bad);
}

/**
 * Life insurer leverage: equity / totalAssets, 30.5% (max) -> 4% (0), single
 * fiscal year. 2026-11-02 strict rebase: ~p95 / ~p33 of the 16 scored live
 * life insurers (low n). Bimodal: reserve-heavy annuity writers (MET, PRU,
 * LNC) run 2-7%, protection writers (AFL, UNM, GL, PRI) 16-25%.
 */
export function lifeInsuranceLeverageValue(equity, totalAssets) {
  if (!isFiniteNumber(equity) || !isPosFinite(totalAssets)) return null;
  const { good, bad } = SECTOR_LEVERAGE_THRESHOLDS.lifeInsurance;
  return leverageRamp(equity / totalAssets, good, bad);
}

/**
 * Other-financial leverage (catch-all): equity / totalAssets, 85% (max) ->
 * 19.5% (0), single fiscal year — 2026-11-02 strict rebase (see the table
 * above for why 85%, not the cleaned p95); no longer shared with bank — UNLESS
 * goodwill + intangibles exceed `OTHER_FIN_GOODWILL_EQUITY_RATIO` (50%) of
 * equity, in which case this
 * switches to the operating-company net-debt/FCF coverage curve instead
 * (see the goodwill-rule note above). The goodwill check runs first and,
 * when it fires, is authoritative — it does not fall back to equity/assets
 * even if `netDebt`/`fcf` are themselves missing (that yields `null` from
 * `operatingLeverageCoverage`, which is correct: a goodwill-heavy company's
 * equity/assets is not a meaningful substitute).
 * Non-positive `equity` with positive `goodwillAndIntangibleAssets` counts as
 * over the threshold (owner decision, 2026-11-01): the override exists for
 * companies whose book equity is not meaningful (buybacks plus goodwill, e.g.
 * MA), and negative equity is the extreme case — without this, GSHD FY2025
 * and MSCI fell through to equity/assets and scored 0 on a negative ratio.
 * `goodwillAndIntangibleAssets` or `equity` missing (not 0) still falls
 * through to the plain equity/assets ramp — the same "missing degrades
 * gracefully, never guesses" rule as the rest of this module.
 */
export function otherFinLeverageValue({
  equity = null, totalAssets = null, goodwillAndIntangibleAssets = null, netDebt = null, fcf = null,
  goodwillHeavy = isGoodwillHeavy(goodwillAndIntangibleAssets, equity),
} = {}) {
  if (goodwillHeavy) {
    return operatingLeverageCoverage(netDebt, fcf);
  }
  if (!isFiniteNumber(equity) || !isPosFinite(totalAssets)) return null;
  const { good, bad } = SECTOR_LEVERAGE_THRESHOLDS.otherFin;
  return leverageRamp(equity / totalAssets, good, bad);
}

/**
 * Classifies a Financial-Services `industry` string (FMP `/stable/profile`)
 * into the 4 leverage buckets this module distinguishes. Live-verified FMP
 * labels (2026-11-01, EPCC_PLAN.md Phase 1 audit):
 *   - Banks: "Banks - Diversified" (JPM, BAC) — matched by a `Banks` prefix
 *     so "Banks - Regional" and any other `Banks - *` variant match too,
 *     without also matching unrelated "Banking"-adjacent labels like
 *     "Investment Banking & Brokerage" (a securities broker, not a
 *     deposit-taking bank — correctly falls through to "other").
 *   - Life insurance: "Insurance - Life" (MET, PRU).
 *   - Insurance: every other "Insurance*" label EXCEPT "Insurance - Brokers"
 *     — "Insurance - Property & Casualty" (PGR, TRV), "Insurance -
 *     Diversified", "Insurance - Specialty", "Insurance - Reinsurance". All
 *     are underwriters carrying claims reserves against their own balance
 *     sheet, so they share the 68.5% -> 21% equity/assets ramp.
 *   - Everything else Financial-Services — "Financial - Credit Services"
 *     (V, MA, GPN), "Asset Management" (ARCC, IVZ), "Insurance - Brokers"
 *     (BWIN — a broker underwrites no risk, so it is not an insurer for
 *     leverage purposes) — falls into "other", the
 *     equity/assets-with-goodwill-override bucket.
 * `null`/non-string `industry` also falls into "other" (degrades to the
 * catch-all rather than guessing a more specific bucket).
 */
export function classifyFinancialLeverageSector(industry) {
  if (typeof industry !== 'string') return 'other';
  if (/^Banks(\s|-|$)/.test(industry)) return 'bank';
  if (industry === 'Insurance - Life') return 'life';
  if (industry === 'Insurance - Brokers') return 'other';
  if (/^Insurance/.test(industry)) return 'insurance';
  return 'other';
}

/**
 * Leverage pillar dispatcher, max 0.30. Replaces the pre-2026-11-01 single
 * formula (now `operatingLeverageCoverage`, used unchanged for the
 * non-financial/non-REIT branch below — every operating-company edge case,
 * including the net-debt <= 0 branches and the documented FCF = 0 cliff, is
 * preserved verbatim).
 *
 * `sector.isReit` takes priority over `sector.isFinancial` (REITs sit under
 * FMP sector "Real Estate", not "Financial Services", so in practice the two
 * never both apply to the same symbol — the priority is a defensive
 * ordering, not something expected to matter live). When `isFinancial` is
 * true, `sector.industry` selects which of the 4 financial-sector helpers
 * above runs; see `classifyFinancialLeverageSector`.
 */
export const leveragePillar = (netDebt, fcf, sector = {}) => {
  const {
    isFinancial = false,
    isReit = false,
    industry = null,
    equity = null,
    totalAssets = null,
    goodwillAndIntangibleAssets = null,
    ebitda = null,
    // Optional routing override (the per-company `latestGoodwillHeavy`);
    // `undefined` falls back to this year's own goodwill test.
    goodwillHeavy = undefined,
  } = sector;

  if (isReit) return reitLeverageValue(netDebt, ebitda);

  if (isFinancial) {
    const bucket = classifyFinancialLeverageSector(industry);
    if (bucket === 'bank') return bankLeverageValue(equity, totalAssets);
    if (bucket === 'insurance') return insuranceLeverageValue(equity, totalAssets);
    if (bucket === 'life') return lifeInsuranceLeverageValue(equity, totalAssets);
    return otherFinLeverageValue({ equity, totalAssets, goodwillAndIntangibleAssets, netDebt, fcf, goodwillHeavy });
  }

  return operatingLeverageCoverage(netDebt, fcf);
};

// ─── Financial equity/assets path: substituted pillars (2026-11-02) ───────
//
// accepted by the owner. ADR 0011's amendment has the evidence.
//
// For a financial year scored on the equity/assets leverage path (bank,
// insurance, life, or other financial without the goodwill override), three
// pillars swap inputs the business model distorts:
//   - FCF quality runs on NET INCOME (growth, growth stability, level
//     stability). A bank's or insurer's operating cash flow is mostly loan,
//     deposit and float flows, not owner earnings. The FCF-vs-earnings term
//     is dropped: it would compare net income with itself.
//   - Revenue quality runs on NET INCOME too. Bank "revenue" is gross
//     interest income, which swung with the 2022-24 rate cycle (JPM FY2023
//     +54%) while net income did not. Net revenue (revenue - interest
//     expense) would be better but is not in the Analysis page's inputs.
//   - Profitability is return on assets (net income / total assets) on a
//     per-bucket ramp, in place of net margin + operating ROA. ROA is the
//     standard bank measure. ROE was rejected because it rewards thin
//     capital and partly undoes the leverage pillar.
//   - The volatility haircut reads net income for both of its series.
//
// STRICT BY OWNER DIRECTIVE: on any substituted pillar a bucket may reach
// the full score at most about half as often as the generic path does, and
// its share at 0 may not exceed the generic path's (the no-collapse limit).
// ROA "good" therefore sits near the 99th percentile of each bucket's scored
// last-5-year ROA (generic profitability maxes in 1.9% of years) and "bad"
// near the 33rd (generic profitability is 0 in 33.2% of years). Implausible
// rows are excluded before taking percentiles: equity <= 0 or missing, total
// assets moving >10x year over year, net income exactly 0. Those rows also
// score a null ROA (see `financialRoaPillar`):
//
//   Bucket      p33     p50     p99      good / bad    max-out  median / p75
//   Generic profitability                              1.9%    0.051 / 0.107
//   Bank        0.93%   1.10%   2.60%    2.6%  / 0.93%   1.0%    0.030 / 0.074
//   Insurance   1.34%   2.68%  18.3%     18.3% / 1.35%   1.0%    0.023 / 0.073
//   Life        0.40%   0.84%  15.8%*    5.1%  / 0.36%   1.3%    0.030 / 0.138
//   Other fin   1.18%   3.02%  26.8%     27%   / 1.2%    1.1%    0.021 / 0.060
//   * life's tail is ABX (a life-settlement manager FMP tags as life
//     insurance); 5.1% leaves every real carrier below the max.
//
// The net-income FCF-quality pillar has no FCF-vs-earnings term, so it tops
// out at 0.30 of its 0.40 cap (the generic path caps at 0.25 since 2026-11-03): its 0.0% max-out rate is built into the
// formula, not measured. Net-income volatility now feeds FCF quality,
// revenue quality AND both haircut factors (strict by owner directive), so
// a volatile earner can lose up to 0.70 x 0.85 = 0.595 of its score to the
// haircut from that one signal.
//
// No sector penalty multiplier and no cap: the failed-bank test (SIVB, FRC,
// SBNY, PACW on FY2021/FY2022 statements) peaks at 58 under these rules,
// below the owner's 80 trigger.
export const SECTOR_ROA_THRESHOLDS = {
  bank: { good: 0.026, bad: 0.0093 },
  insurance: { good: 0.183, bad: 0.0135 },
  life: { good: 0.051, bad: 0.0036 },
  other: { good: 0.27, bad: 0.012 },
};

/**
 * True when this year is a financial scored on the equity/assets leverage
 * path: `isFinancial`, not a REIT, and not caught by the other-financial
 * goodwill override (which stays on the generic curves for every pillar).
 */
export function onFinancialEquityAssetsPath({
  isFinancial = false, isReit = false, industry = null, equity = null, goodwillAndIntangibleAssets = null,
  goodwillHeavy = isGoodwillHeavy(goodwillAndIntangibleAssets, equity),
} = {}) {
  if (!isFinancial || isReit) return false;
  if (classifyFinancialLeverageSector(industry) !== 'other') return true;
  return !goodwillHeavy;
}

/**
 * Goodwill-override status used to route the whole financial path: leverage,
 * profitability, FCF quality, revenue quality and the haircut. It is the
 * status of the LATEST year with known equity (an exact-0 or missing equity
 * is unknown), applied to every year, `false` when no year is known. Whether
 * a financial's book equity and reported cash flow mean anything is a
 * property of the business, so it is judged once per company on its current
 * balance sheet: a goodwill-override name (V, MA, CME) is scored exactly like
 * a non-financial in every year, including early years when its goodwill was
 * under half of equity or a balance row is missing.
 */
export function latestGoodwillHeavy(rows) {
  for (let k = rows.length - 1; k >= 0; k--) {
    if (rows[k] && isFiniteNumber(rows[k].equity)) {
      return isGoodwillHeavy(rows[k].goodwillAndIntangibleAssets, rows[k].equity);
    }
  }
  return false;
}

/** Net income as the financial path reads it: FMP's exact 0 is "no figure", so missing. */
export const financialNetIncome = (row) => (
  row && isFiniteNumber(row.netIncome) && row.netIncome !== 0 ? row.netIncome : null
);

/**
 * Profitability on the equity/assets path, max 0.30: return on assets
 * (net income / total assets) on the bucket's `SECTOR_ROA_THRESHOLDS` ramp.
 * `null` when net income is missing or exactly 0 (FMP's "no figure"), total
 * assets are missing or not positive, or equity is missing or not positive
 * (an exact-0 equity is FMP's "no figure"; a financial with no positive book
 * equity has no usable balance sheet to earn a return on, and its leverage
 * pillar already scores 0), or total assets moved more than
 * `FINANCIAL_ASSET_JUMP_LIMIT` (10x) either way against `priorTotalAssets`
 * (the previous consecutive year's): a re-based or broken balance sheet
 * (KKRS FY2024 $317B -> $1.0B, SBET FY2024, ABTC FY2024). The same
 * plausibility rule excluded rows when the ROA anchors were derived.
 */
export const FINANCIAL_ASSET_JUMP_LIMIT = 10;

export function financialRoaPillar(netIncome, totalAssets, industry, equity, priorTotalAssets = null) {
  if (!isFiniteNumber(netIncome) || netIncome === 0 || !isPosFinite(totalAssets) || !isPosFinite(equity)) return null;
  if (isPosFinite(priorTotalAssets)) {
    const move = totalAssets / priorTotalAssets;
    if (move > FINANCIAL_ASSET_JUMP_LIMIT || move < 1 / FINANCIAL_ASSET_JUMP_LIMIT) return null;
  }
  const { good, bad } = SECTOR_ROA_THRESHOLDS[classifyFinancialLeverageSector(industry)];
  return leverageRamp(netIncome / totalAssets, good, bad, QUALITY_CAPS.profitability.max);
}

/**
 * FCF quality, floored at 0. Generic path: growth and FCF-vs-earnings terms
 * only, max 0.25 (the stability terms moved into the consistency pillar,
 * 2026-11-03). Financial equity/assets path (`financialPath`): unchanged, with
 * both stability terms, max 0.40. Verbatim from qualityScore.js.
 * A floor is the more defensible design on its own terms: a single bad year
 * should stop contributing to the score, not actively subtract from it.
 * Legacy's `fcf_quality.clip(lower=0)` (data_refinery.py:529) has the same
 * floor, which corroborates this but is not the reason for it.
 */
export const fcfQualityPillar = ({
  fcf = null, growthWindow = null, levelWindow = null, earningsRatioWindow = null, financialPath = false,
} = {}) => {
  if (!isFiniteNumber(fcf)) return null;
  if (!(fcf > 0)) return 0;

  const caps = QUALITY_CAPS.fcf;
  let sum = 0;
  let any = false;

  if (finiteWindow(growthWindow)) {
    const clipped = clamp(mean(growthWindow), -caps.growthClip, caps.growthClip);
    sum += clipped * caps.growthScale * caps.growthWeight;
    any = true;
    if (financialPath) {
      const stability = stabilityTerm(growthWindow, caps.growthCvCeiling);
      if (stability != null) sum += stability;
    }
  }

  if (finiteWindow(earningsRatioWindow)) {
    sum += clamp(mean(earningsRatioWindow), -caps.earningsClip, caps.earningsClip);
    any = true;
  }

  // Generic path: no level-stability term, but a computable one still makes
  // the pillar non-null (0 rather than null), as before 2026-11-03.
  if (finiteWindow(levelWindow)) {
    const stability = stabilityTerm(levelWindow, caps.levelCvCeiling);
    if (stability != null) {
      if (financialPath) sum += stability;
      any = true;
    }
  }

  if (!any) return null;
  return clamp(sum, 0, financialPath ? caps.financialMax : caps.max);
};

/**
 * Revenue quality, floored at 0. Generic path: growth term only, max 0.15
 * (stability terms removed 2026-11-03). Financial equity/assets path
 * (`financialPath`): unchanged, with both stability terms, max 0.30. Verbatim from qualityScore.js.
 * Same floor-at-0 reasoning as the FCF pillar above, plus a ±8% clip on the
 * 3-year average growth. The clip reuses FCF's existing `growthClip` (0.08)
 * and `growthScale` (1.25) constants verbatim for consistency within this
 * module — no new numbers introduced. Legacy (data_refinery.py lines 539,
 * 545) has the same clip magnitude and the same floor, which corroborates
 * both but is not the reason for either; the ±8% figure has not been
 * independently retuned.
 */
export const revenueQualityPillar = ({ growthWindow = null, levelWindow = null, financialPath = false } = {}) => {
  const caps = QUALITY_CAPS.revenue;
  let sum = 0;
  let any = false;

  if (finiteWindow(growthWindow)) {
    const { growthClip, growthScale } = QUALITY_CAPS.fcf;
    const clipped = clamp(mean(growthWindow), -growthClip, growthClip);
    sum += clipped * growthScale * caps.growthWeight;
    any = true;
    if (financialPath) {
      const stability = stabilityTerm(growthWindow, caps.growthCvCeiling);
      if (stability != null) sum += stability;
    }
  }

  // Generic path: no level-stability term, but a computable one still makes
  // the pillar non-null (0 rather than null), as before 2026-11-03.
  if (finiteWindow(levelWindow)) {
    const stability = stabilityTerm(levelWindow, caps.levelCvCeiling);
    if (stability != null) {
      if (financialPath) sum += stability;
      any = true;
    }
  }

  if (!any) return null;
  return clamp(sum, 0, financialPath ? caps.financialMax : caps.max);
};

/** Sum of the pillars that had data, clipped to [0, 1]. Verbatim. */
export const qualityFromPillars = (pillars = {}) => {
  const values = ['profitability', 'dilution', 'leverage', 'fcf', 'revenue']
    .map((key) => pillars[key])
    .filter(isFiniteNumber);
  if (!values.length) return null;
  const sum = values.reduce((total, value) => total + value, 0);
  // Consistency is added last and never makes a year score on its own.
  const consistency = isFiniteNumber(pillars.consistency) ? pillars.consistency : 0;
  return clamp(sum + consistency, QUALITY_CAPS.quality.min, QUALITY_CAPS.quality.max);
};

/**
 * FCF after stock-based compensation: `fcf - max(sbc, 0)` (a reported SBC
 * credit never adds to FCF). Feeds the FCF-quality pillar and the volatility
 * haircut only; leverage stays on reported FCF. Returns `fcf` unchanged when
 * `sbc` is not a finite number.
 */
export function ownerEarningsFcf(fcf, sbc) {
  if (!isFiniteNumber(fcf) || !isFiniteNumber(sbc)) return fcf;
  return fcf - Math.max(sbc, 0);
}

/**
 * Post-clamp volatility haircut. Identical in funviz-amplify's houseScores.mjs
 * and funviz-react's qualityScore.js (keep the two in sync by hand).
 *
 * Inputs are the TRAILING 5 consecutive fiscal years of revenue and of FCF
 * after SBC. Each series gets a factor in [floor, 1]:
 *   - fewer than 5 consecutive finite values           -> 1 (no haircut)
 *   - FCF only: 5-year mean <= 0                       -> 1 (the FCF pillar
 *     already scores 0, so no second penalty)
 *   - coefficient of variation (pop. std / |mean|) < 0.10 -> 1 (flat series)
 *   - trend stability `seriesStability` (Pearson r vs time) null -> 1
 *   - r >= hi -> 1; r <= lo -> floor; linear in between.
 * Revenue: hi 0.6, lo -0.2, floor 0.70. FCF: hi 0.3, lo -0.5, floor 0.85.
 * The two factors multiply.
 */
export const VOLATILITY_HAIRCUT = {
  window: 5,
  cvExempt: 0.10,
  revenue: { hi: 0.6, lo: -0.2, floor: 0.70 },
  fcf: { hi: 0.3, lo: -0.5, floor: 0.85 },
};

/**
 * Pearson correlation of the series against its index, rounded to 3 dp; null
 * for fewer than 3 points or a flat series. Copy of the screener's
 * metrics.mjs `stability()` (the haircut was simulated against it).
 */
export const seriesStability = (series) => {
  const ys = (series || []).filter(isFiniteNumber);
  if (ys.length < 3) return null;
  const xs = ys.map((_, i) => i);
  const mx = mean(xs);
  const my = mean(ys);
  const num = xs.reduce((a, x, i) => a + (x - mx) * (ys[i] - my), 0);
  const dx = Math.sqrt(xs.reduce((a, x) => a + (x - mx) ** 2, 0));
  const dy = Math.sqrt(ys.reduce((a, y) => a + (y - my) ** 2, 0));
  if (dx === 0 || dy === 0) return null;
  return Math.round((num / (dx * dy)) * 1000) / 1000;
};

export const haircutFactor = (series, { hi, lo, floor }, isFcf) => {
  if (!Array.isArray(series) || series.length !== VOLATILITY_HAIRCUT.window || !series.every(isFiniteNumber)) return 1;
  const center = mean(series);
  if (isFcf && !(center > 0)) return 1;
  const cv = center !== 0 ? populationStdev(series) / Math.abs(center) : Infinity;
  if (cv < VOLATILITY_HAIRCUT.cvExempt) return 1;
  const s = seriesStability(series);
  if (s == null || s >= hi) return 1;
  if (s <= lo) return floor;
  return 1 - (1 - floor) * (hi - s) / (hi - lo);
};

export const volatilityHaircut = ({ revenueSeries = null, fcfSeries = null } = {}) => (
  haircutFactor(revenueSeries, VOLATILITY_HAIRCUT.revenue, false)
  * haircutFactor(fcfSeries, VOLATILITY_HAIRCUT.fcf, true)
);

/**
 * Final per-year quality fraction: the pillar sum clamped to [0, 1]
 * (`qualityFromPillars`) times the volatility haircut.
 */
export const finalizeQualityFraction = (pillars, { revenueSeries = null, fcfSeries = null } = {}) => {
  const summed = qualityFromPillars(pillars);
  if (summed == null) return null;
  return summed * volatilityHaircut({ revenueSeries, fcfSeries });
};

/**
 * EBITDA margin = EBITDA / revenue. Verbatim from qualityScore.js. An exact 0
 * EBITDA is MISSING, not a 0% margin: FMP writes 0 when it has no figure
 * (AMSF FY2023, MSIF FY2022-23, KKRS FY2025: ebitda 0 beside a positive
 * operatingIncome) — same convention as `guardedProfitabilityMargin`.
 */
export const ebitdaMarginFrom = (ebitda, revenue) => {
  if (!isFiniteNumber(ebitda) || ebitda === 0 || !isFiniteNumber(revenue) || !(revenue > 0)) return null;
  return ebitda / revenue;
};

/**
 * Operating-cash-flow margin = operatingCashFlow / revenue — the Growth
 * score's REIT margin basis (2026-11-01, see the "Growth-score margin" block
 * below). Same gap rules as `ebitdaMarginFrom`.
 */
export const ocfMarginFrom = (operatingCashFlow, revenue) => {
  if (!isFiniteNumber(operatingCashFlow) || !isFiniteNumber(revenue) || !(revenue > 0)) return null;
  return operatingCashFlow / revenue;
};

/**
 * Year-over-year growth (current - prior) / |prior|. Verbatim from
 * funviz-react's annualGrowth.js `yoyGrowth`.
 */
export function yoyGrowth(current, prior) {
  if (!isFiniteNumber(current)) return null;
  if (!isFiniteNumber(prior) || prior === 0) return null;
  return (current - prior) / Math.abs(prior);
}

// ─── Quality-score rework (2026-10-30) — operating-margin guard, 3Y-mean-FCF
//     leverage input, strict 3/5/10-year averaged headline(s) ────────────
//
// Three owner-approved changes to how the QUALITY score (not Growth) is fed
// and reported, driven by a full-universe finding: 28.5% of companies swung
// >20 points year-to-year on the single-latest-year basis, 12.7% sat at
// exactly 0, 2.8% at exactly 100 — e.g. ATEX (Anterix) hit 100 in FY2026 on a
// one-off non-operating gain (net margin 1394%, operating margin -640%).
//
// 1. OPERATING-MARGIN GUARD on the profitability pillar's margin input: for
//    any fiscal year whose `operatingIncome` is a FINITE, NON-ZERO number,
//    feed `min(netMargin, operatingMargin)` instead of `netMargin` alone.
//    FMP reports exactly 0 for `operatingIncome` when it genuinely has no
//    data for that line (not a real breakeven year) — 0 is therefore treated
//    as MISSING, same "0 !== null" convention as the rest of this module.
//    When operatingIncome is missing (null/undefined/NaN/0) or revenue isn't
//    positive (no margin ratio is possible at all), this degrades UNCHANGED
//    to `netMargin` — today's pre-guard behaviour. The guard can only LOWER
//    the fed-in margin (a `min`), never raise it. See
//    `guardedProfitabilityMargin` below.
//
// 2. LEVERAGE PILLAR FED THE MEAN OF THE LAST `QUALITY_LEVERAGE_FCF_WINDOW`
//    (3) CONSECUTIVE FISCAL YEARS' FCF, not that single year's FCF alone —
//    same `leveragePillar(netDebt, fcf, sector)` function, same
//    net-cash/net-debt branches, just a smoothed `fcf` input. This is
//    internal INPUT SMOOTHING, not a window-labeled headline metric, so
//    graceful degradation is correct here: when 3 consecutive years of FCF
//    aren't available for a given fiscal year, that year falls back to its
//    OWN single-year FCF (today's behaviour) rather than going null. See
//    `fcfWindowMean` below.
//
// 3. THE HEADLINE `qualityScore` IS NOW A STRICT 3-YEAR AVERAGE of the
//    per-fiscal-year quality score (each year computed with changes 1 and 2
//    applied) — not the latest year alone. "Strict" means: non-null ONLY if
//    the last 3 fiscal years are 3 CONSECUTIVE years (year, year-1, year-2)
//    AND EVERY ONE of those 3 years has a fully-computed (non-null) per-year
//    quality score; otherwise `qualityScore` is `null`. This matches this
//    project's existing convention that a window-labeled metric (e.g. a
//    `*Cagr10`) is null rather than a shorter span silently wearing the
//    wrong label. Measured ~8% of the universe goes null under this rule
//    (mostly young IPOs with <3 fully-scored years) — expected, not a bug.
//
//    `qualityScore5y` and `qualityScore10y` are NEW, ADDITIVE fields (not a
//    replacement for `qualityScore`, and not a third headline) computed the
//    SAME way with a 5- and 10-consecutive-year window respectively — same
//    strict null rule, same per-year quality score, just a longer average.
//    The leverage pillar's own FCF-smoothing window (change 2) stays fixed
//    at `QUALITY_LEVERAGE_FCF_WINDOW` (3) for every per-year computation,
//    REGARDLESS of which of the three averaging windows is consuming that
//    year's score — the FCF-smoothing window and the headline-averaging
//    window are two independent constants that happen to both start at 3.
//
// `computeYearQualityFraction` is the per-fiscal-year engine (exported for
// direct unit testing and for the verification technique below); it reuses
// every pillar function UNCHANGED (profitabilityPillar, dilutionPillar,
// leveragePillar, fcfQualityPillar, revenueQualityPillar, qualityFromPillars)
// — only what's fed INTO them changed. The operating-margin guard and the
// leverage FCF-smoothing window are always on: the `applyOperatingMarginGuard`
// and `applyFcfSmoothing` options that once switched them off for a
// regression-anchor test were removed on 2026-11-04 and are not a runtime
// toggle.
export const QUALITY_LEVERAGE_FCF_WINDOW = 3;

export const QUALITY_AVERAGE_WINDOWS = {
  qualityScore: 3,
  qualityScore5y: 5,
  qualityScore10y: 10,
};

/** True iff fiscal years `i` and `j` in `rows` are adjacent and consecutive (rows[i].year === rows[j].year + 1). */
export function consecutiveYears(rows, i, j) {
  return (
    i >= 0 && j >= 0 && i < rows.length && j < rows.length
    && rows[i].year != null && rows[j].year != null && rows[i].year === rows[j].year + 1
  );
}

/** True iff `i`, `i-1`, `i-2` are 3 consecutive fiscal years — the FCF-quality/revenue-quality pillars' own 3-point window, at an arbitrary index. */
export function threeConsecutiveAt(rows, i) {
  return i >= 2 && consecutiveYears(rows, i, i - 1) && consecutiveYears(rows, i - 1, i - 2);
}

/** YoY growth of `pick(rows[i])` vs `pick(rows[i-1])`, gated on those two indices being consecutive fiscal years. */
export function growthAtYear(rows, pick, i) {
  return consecutiveYears(rows, i, i - 1) ? yoyGrowth(pick(rows[i]), pick(rows[i - 1])) : null;
}

/**
 * Change 1 (operating-margin guard). `revenue` is required only to turn
 * `operatingIncome` into a margin ratio — it plays no other role, and when
 * it's non-positive the guard can't form a ratio at all so it degrades to
 * `netMargin`, exactly like the "missing operatingIncome" case.
 */
export function guardedProfitabilityMargin({ netMargin = null, operatingIncome = null, revenue = null } = {}) {
  const hasOperatingIncome = isFiniteNumber(operatingIncome) && operatingIncome !== 0;
  if (!hasOperatingIncome || !isPosFinite(revenue) || !isFiniteNumber(netMargin)) return netMargin;
  const operatingMargin = operatingIncome / revenue;
  return Math.min(netMargin, operatingMargin);
}

/**
 * Change 2 (leverage's FCF input). Mean FCF over the `windowSize` most
 * recent CONSECUTIVE fiscal years ending at index `i`. Returns `null` —
 * meaning "the caller should fall back to that single year's own FCF", NOT
 * "leverage is unknown" — when `windowSize` consecutive years aren't all
 * present with a finite FCF each.
 */
export function fcfWindowMean(rows, i, windowSize) {
  const firstIndex = i - windowSize + 1;
  if (firstIndex < 0) return null;
  for (let k = i; k > firstIndex; k--) {
    if (!consecutiveYears(rows, k, k - 1)) return null;
  }
  const slice = rows.slice(firstIndex, i + 1).map((r) => r.fcf);
  if (!slice.every(isFiniteNumber)) return null;
  return mean(slice);
}

/**
 * Earnings the FCF-quality pillar's FCF-vs-earnings term compares FCF with.
 * Net income for everyone except a REIT, whose net income is after real-estate
 * depreciation that capex does not match (property is bought, not "replaced"
 * at its depreciation), so FCF reads a median of ~1.6x net income for equity REITs with NI > 0,
 * and the term sat at its +0.10 clip in 86% of REIT-years (85.7% equity, 88.6%
 * mortgage; generic 54%). A REIT compares against net income + D&A, an FFO
 * proxy (not FFO), the cash-earnings analogue. A
 * missing D&A adds nothing. 2026-11-03, ADR 0011 amendment.
 */
export function fcfEarningsBasis(row, isReit) {
  if (!isReit || !isFiniteNumber(row.netIncome)) return row.netIncome;
  return row.netIncome + (isFiniteNumber(row.depreciationAndAmortization) ? row.depreciationAndAmortization : 0);
}

/**
 * Per-fiscal-year Quality engine at index `i`: `{ pillars, fraction }`, where
 * `fraction` is 0-1 (or null) and `pillars` are the per-pillar values, or null
 * when there is no row at `i`. Every pillar reads the rows it is given, so the
 * caller must hand over rows built by `buildQualityRows`.
 */
export function computeYearQuality(rows, i, {
  operatingRoaByDate = null,
  isFinancial = false,
  isReit = false,
  industry = null,
} = {}) {
  const row = rows[i];
  if (!row) return null;
  const prevRow = rows[i - 1] || null;
  const hasPrevYear = consecutiveYears(rows, i, i - 1);
  const threeConsecutive = threeConsecutiveAt(rows, i);

  const netMarginForPillar = guardedProfitabilityMargin({ netMargin: row.margin, operatingIncome: row.operatingIncome, revenue: row.revenue });

  const operatingRoaFraction = operatingRoaByDate && row.date != null && operatingRoaByDate.has(row.date)
    ? operatingRoaByDate.get(row.date)
    : null;

  const fcfForLeverage = fcfWindowMean(rows, i, QUALITY_LEVERAGE_FCF_WINDOW) ?? row.fcf;

  // FCF after SBC feeds the FCF-quality pillar and the haircut only; leverage
  // above stays on reported FCF. A row with no `sbc` degrades to reported FCF.
  const fcfAfterSbc = (r) => ownerEarningsFcf(r.fcf, r.sbc);
  const revenueWindow3 = threeConsecutive ? [rows[i - 2].revenue, rows[i - 1].revenue, row.revenue] : null;
  const fcfWindow3 = threeConsecutive ? [fcfAfterSbc(rows[i - 2]), fcfAfterSbc(rows[i - 1]), fcfAfterSbc(row)] : null;
  // Haircut inputs: trailing 5 consecutive fiscal years (null otherwise -> no haircut).
  const haircutWindow = VOLATILITY_HAIRCUT.window;
  const has5 = isConsecutiveWindow(rows, i, haircutWindow);
  const haircutRows = has5 ? rows.slice(i - haircutWindow + 1, i + 1) : null;

  // Financial equity/assets path (2026-11-02): profitability, FCF quality,
  // revenue quality and the haircut read net income — see the block after
  // `leveragePillar`. Every other year keeps the generic inputs below. One
  // routing rule for the whole financial path, leverage included: the
  // goodwill override is judged once per company, on the latest year with
  // known equity (`latestGoodwillHeavy`).
  const companyGoodwillHeavy = latestGoodwillHeavy(rows);
  const financialPath = onFinancialEquityAssetsPath({
    isFinancial, isReit, industry, goodwillHeavy: companyGoodwillHeavy,
  });
  const netIncomeOf = financialNetIncome;
  const netIncomeGrowthWindow = threeConsecutive
    ? [growthAtYear(rows, netIncomeOf, i - 2), growthAtYear(rows, netIncomeOf, i - 1), growthAtYear(rows, netIncomeOf, i)]
    : null;
  const netIncomeWindow3 = threeConsecutive ? [rows[i - 2], rows[i - 1], row].map(netIncomeOf) : null;

  const pillars = {
    profitability: financialPath
      ? financialRoaPillar(row.netIncome, row.totalAssets, industry, row.equity, hasPrevYear ? prevRow.totalAssets : null)
      : profitabilityPillar({ netMargin: netMarginForPillar, operatingRoa: operatingRoaFraction }),
    dilution: hasPrevYear ? dilutionPillar(row.shares, prevRow.shares) : null,
    leverage: leveragePillar(row.netDebt, fcfForLeverage, {
      isFinancial,
      isReit,
      industry,
      equity: row.equity,
      totalAssets: row.totalAssets,
      goodwillAndIntangibleAssets: row.goodwillAndIntangibleAssets,
      ebitda: isReit ? reitLeverageEbitda(rows, i) : null,
      goodwillHeavy: companyGoodwillHeavy,
    }),
    fcf: financialPath ? fcfQualityPillar({
      fcf: netIncomeOf(row), growthWindow: netIncomeGrowthWindow, levelWindow: netIncomeWindow3, earningsRatioWindow: null, financialPath: true,
    }) : fcfQualityPillar({
      fcf: fcfAfterSbc(row),
      growthWindow: threeConsecutive
        ? [
          growthAtYear(rows, fcfAfterSbc, i - 2),
          growthAtYear(rows, fcfAfterSbc, i - 1),
          growthAtYear(rows, fcfAfterSbc, i),
        ]
        : null,
      levelWindow: fcfWindow3,
      earningsRatioWindow: threeConsecutive
        ? [i - 2, i - 1, i].map((k) => {
          const item = rows[k];
          const itemFcf = fcfAfterSbc(item);
          const earnings = fcfEarningsBasis(item, isReit);
          if (!isFiniteNumber(itemFcf) || !isFiniteNumber(earnings) || earnings === 0) return null;
          return (itemFcf - earnings) / Math.abs(earnings);
        })
        : null,
    }),
    revenue: financialPath
      ? revenueQualityPillar({ growthWindow: netIncomeGrowthWindow, levelWindow: netIncomeWindow3, financialPath: true })
      : revenueQualityPillar({
        growthWindow: threeConsecutive
          ? [growthAtYear(rows, (r) => r.revenue, i - 2), growthAtYear(rows, (r) => r.revenue, i - 1), growthAtYear(rows, (r) => r.revenue, i)]
          : null,
        levelWindow: revenueWindow3,
      }),
    // Generic path only; see consistencyPillar.
    consistency: financialPath ? null : consistencyPillar(rows, i),
  };
  return {
    pillars,
    fraction: finalizeQualityFraction(pillars, {
      revenueSeries: haircutRows ? haircutRows.map(financialPath ? netIncomeOf : (r) => r.revenue) : null,
      fcfSeries: haircutRows ? haircutRows.map(financialPath ? netIncomeOf : fcfAfterSbc) : null,
    }),
  };
}

/** Per-fiscal-year Quality fraction (0-1, or null) at index `i`; see `computeYearQuality`. */
export function computeYearQualityFraction(rows, i, ctx = {}) {
  return computeYearQuality(rows, i, ctx)?.fraction ?? null;
}


/**
 * Shared strict-window check: true iff the `windowSize` fiscal years ending
 * at `lastIndex` are `windowSize` CONSECUTIVE years (year, year-1, …).
 * Factored out of `averageQualityWindow` (2026-10-30) so the Growth-score
 * 5/10-year averages below (`averageGrowthWindow`) reuse the exact same
 * consecutive-years check rather than forking a second copy of it — only
 * the "every year's per-year score must also be non-null" half differs
 * between the two callers, since Quality and Growth read that score from
 * different per-year engines (`computeYearQualityFraction` vs
 * `computeYearGrowthScores`).
 */
export function isConsecutiveWindow(rows, lastIndex, windowSize) {
  const firstIndex = lastIndex - windowSize + 1;
  if (firstIndex < 0) return false;
  for (let k = lastIndex; k > firstIndex; k--) {
    if (!consecutiveYears(rows, k, k - 1)) return false;
  }
  return true;
}

// ─── Consistency pillar (2026-11-03, owner-approved) ───────────────────────
//
// Generic path only (null on the financial equity/assets path). Rewards a
// business whose earnings per share rise year after year on steady operating
// margins. Two components, each in [-1, 1]:
//   e = EPS direction over the 6 consecutive fiscal years ending at i:
//       2 × up / 5 − 1, where `up` counts the 5 YoY changes with EPS not
//       falling AND EPS > 0. EPS = netIncome / weightedAverageShsOut.
//   m = operating-margin stability over the 5 consecutive years ending at i:
//       inverse CV (mean / population std) ramped 0 at 2 to 1 at 10; a
//       negative mean is −1; a zero std is the sign of the mean.
// Either component null (too few consecutive years, or a missing input in its
// window) -> the pillar is 0. Else 0.40 × clamp(0.5e + 0.5m, −1, 1).
// Evidence: funviz-amplify docs/adr/0015-quality-consistency-pillar.md.
// Byte-for-byte shared between houseScores.mjs and qualityScore.js.

export const epsOfRow = (row) => (
  isFiniteNumber(row.netIncome) && row.shares > 0 ? row.netIncome / row.shares : null
);

export const consistencyEpsComponent = (rows, i) => {
  const { epsYears } = QUALITY_CAPS.consistency;
  if (!isConsecutiveWindow(rows, i, epsYears)) return null;
  const eps = rows.slice(i - epsYears + 1, i + 1).map(epsOfRow);
  if (!eps.every(isFiniteNumber)) return null;
  let up = 0;
  for (let k = 1; k < eps.length; k++) {
    if (eps[k] >= eps[k - 1] && eps[k] > 0) up++;
  }
  const share = up / (eps.length - 1);
  return 2 * share - 1;
};

export const consistencyMarginComponent = (rows, i) => {
  const { marginYears, marginInverseCvZero, marginInverseCvFull } = QUALITY_CAPS.consistency;
  if (!isConsecutiveWindow(rows, i, marginYears)) return null;
  const margins = rows.slice(i - marginYears + 1, i + 1).map((row) => (
    isFiniteNumber(row.operatingIncome) && row.revenue > 0 ? row.operatingIncome / row.revenue : null
  ));
  if (!margins.every(isFiniteNumber)) return null;
  const center = mean(margins);
  const std = populationStdev(margins);
  if (std === 0) {
    if (center > 0) return 1;
    return center < 0 ? -1 : 0;
  }
  const inverseCv = center / std;
  if (inverseCv < 0) return -1;
  return clamp((inverseCv - marginInverseCvZero) / (marginInverseCvFull - marginInverseCvZero), 0, 1);
};

export const consistencyPillar = (rows, i) => {
  const e = consistencyEpsComponent(rows, i);
  const m = consistencyMarginComponent(rows, i);
  if (e == null || m == null) return 0;
  const { max, epsWeight, marginWeight } = QUALITY_CAPS.consistency;
  return clamp(epsWeight * e + marginWeight * m, -1, 1) * max;
};

/**
 * Change 3's strict averaging rule for ONE window size, shared by
 * `qualityScore`/`qualityScore5y`/`qualityScore10y`. Non-null only if the
 * `windowSize` fiscal years ending at `lastIndex` are `windowSize`
 * CONSECUTIVE years AND every one of them has a non-null per-year quality
 * score (guard + FCF-smoothing always applied — this path never disables
 * them). Averages the already-rounded 0-100 per-year scores, not the raw
 * fractions, then rounds the mean to 2dp.
 */
export function averageQualityWindow(rows, lastIndex, windowSize, ctx) {
  if (!isConsecutiveWindow(rows, lastIndex, windowSize)) return null;
  const firstIndex = lastIndex - windowSize + 1;
  const perYearScores = [];
  for (let k = firstIndex; k <= lastIndex; k++) {
    const fraction = computeYearQualityFraction(rows, k, ctx);
    if (!isFiniteNumber(fraction)) return null;
    perYearScores.push(round(fraction * 100, 2));
  }
  return round(mean(perYearScores), 2);
}

// ─── Growth-score averages (2026-10-30 5/10y; 2026-11-01 3y headline) ──────
//
// Every Growth field is a strict average of the per-fiscal-year score from
// `computeYearGrowthScores` (below), the same way Quality's per-year engine
// (`computeYearQualityFraction`) feeds `qualityScore`/5y/10y:
// `growthScoreTotal`/`growthScorePerShare` over `GROWTH_HEADLINE_WINDOW` (3)
// years, the 5y/10y fields over `GROWTH_AVERAGE_WINDOWS`. Null unless the
// window is that many consecutive fiscal years and every year scores. The 3y
// headline replaced the single latest year on 2026-11-01: a merger or
// one-off year no longer sets the headline on its own (UNIT 100 -> 53).
export const GROWTH_HEADLINE_WINDOW = 3;

export const GROWTH_AVERAGE_WINDOWS = [5, 10];

// ─── Growth-score per-year rule: additive Rule of 40 with above-peak growth
//     and a rising-years factor (2026-11-02, owner-approved; amended
//     2026-11-03; supersedes ADR 0011's) ────────────────────────────────────
//
// Decision record and research results:
// docs/adr/0012-growth-score-additive-rule-of-40.md (supersedes the Growth
// part of docs/adr/0011-growth-score-margin-cap-and-floor.md). Per fiscal
// year t with a consecutive prior year, inside `computeYearGrowthScores`:
//
//   g      = YoY revenue growth. Total: min(total growth, per-share growth)
//            (per share = revenue / weightedAverageShsOut; total alone when
//            per share is missing), so growth bought with new shares does
//            not count. Per share: per-share growth alone.
//   peak   = the highest TOTAL revenue of the prior GROWTH_PEAK_WINDOW (5)
//            consecutive fiscal years (an exact 0 is skipped).
//   above  = the share of this year's increase that lies above that peak
//            (growthPeakFactor): 1 when the base year is the peak,
//            (rev[t] − peak) / (rev[t] − rev[t−1]) when the year ends above a
//            higher earlier peak, 0 when it ends at or below it. For total
//            revenue, g × above = max(0, rev[t] − peak) / rev[t−1]: only
//            growth to a new 5-year high counts; recovering toward an old
//            peak earns nothing (scope: ADR 0012 "Scope of the Growth score").
//   trend  = growthTrendFactor(risingYearShare): the share of the last
//            GROWTH_TREND_WINDOW (10) consecutive YoY revenue changes ending
//            at t that did not fall, ramped linearly from 0 at
//            GROWTH_TREND_ZERO_SHARE (0.4) to 1 at GROWTH_TREND_FULL_SHARE
//            (0.8); 1 with fewer than GROWTH_TREND_MIN_CHANGES (5) changes.
//            Catches new highs after a long trough (NGS).
//   g_eff  = min(g × above × trend, GROWTH_GROWTH_CAP (100%)) when g > 0,
//            else g. The same factor serves both columns, so Per Share ≥
//            Total always.
//   m      = that year's own margin: EBITDA / revenue (an exact-0 EBITDA is
//            missing); operating cash flow / revenue for a REIT; 0 for a
//            lender (isGrowthLender), whose every revenue read above and
//            below is lenderNetRevenue (revenue − interest expense).
//   raw    = clamp((g_eff + 0.75 × clamp(m, −1.00, +0.40)) / 0.70, 0, 1) × 100
//   ceiling= clamp(g_eff / 0.15, 0, 1) × 100      (0% or less -> 0, 5% -> 33.3)
//   score  = min(raw, ceiling), rounded to 2 dp. The continuous ceiling
//            (2026-11-02) replaced a 2% / 30 floor: no cliff, and a flat
//            high-margin year no longer scores ~46.
//   A year whose own or base-year revenue is ≤ 0 (exact 0 included) scores
//   0 on both columns; a missing revenue, row or consecutive year is null.
//
// Headline (and the 5y/10y fields) = strict mean of the per-year scores,
// capped at 30 when any revenue in the window or its base year is ≤ 0.
// What it replaced: ADR 0011's 3-year-mean margin, its 75% margin cap and
// `clamp(margin + growth, 0, 1)`. The sector leverage pillar and the rest of
// Quality are untouched.
export const GROWTH_PEAK_WINDOW = 5;

export const GROWTH_TREND_WINDOW = 10;

export const GROWTH_TREND_MIN_CHANGES = 5;

export const GROWTH_TREND_ZERO_SHARE = 0.4;

export const GROWTH_TREND_FULL_SHARE = 0.8;

export const GROWTH_GROWTH_CAP = 1.0;

export const GROWTH_MARGIN_WEIGHT = 0.75;

export const GROWTH_MARGIN_CAP = 0.40;

export const GROWTH_MARGIN_FLOOR = -1.0;

export const GROWTH_FULL_SCORE = 0.70;

export const GROWTH_CEILING_GROWTH = 0.15;

export const GROWTH_NON_POSITIVE_REVENUE_CAP = 30;

// Lenders (ADR 0012 amendment 2026-11-03). A financial, non-REIT symbol whose
// LATEST fiscal year has interest income ≥ 50% of revenue scores Growth on
// revenue net of interest expense (gross interest income rides the rate
// cycle), with margin 0 (bank EBITDA / revenue is not like-for-like). A year
// whose interest expense is missing or exactly 0 has no net revenue (a gap).
// Mirrored in funviz-react qualityScore.js.
export const GROWTH_LENDER_INTEREST_SHARE = 0.5;

export function isGrowthLender(rows, isFinancial, isReit) {
  const row = rows[rows.length - 1];
  return !!isFinancial && !isReit && !!row && row.revenue > 0 && isFiniteNumber(row.interestIncome)
    && row.interestIncome / row.revenue >= GROWTH_LENDER_INTEREST_SHARE;
}

export const lenderNetRevenue = (row) => (
  row && isFiniteNumber(row.revenue) && isFiniteNumber(row.interestExpense) && row.interestExpense > 0
    ? row.revenue - row.interestExpense : null);

/**
 * Mean of `pick(row)` over the `windowSize` most recent CONSECUTIVE fiscal
 * years ending at index `i`. Same "null means fall back to the single year"
 * contract as the leverage pillar's `fcfWindowMean` (left untouched as a
 * separate, tested function). Used by `reitLeverageEbitda`.
 */
export function consecutiveWindowMean(rows, i, windowSize, pick) {
  const firstIndex = i - windowSize + 1;
  if (firstIndex < 0) return null;
  for (let k = i; k > firstIndex; k--) {
    if (!consecutiveYears(rows, k, k - 1)) return null;
  }
  const slice = rows.slice(firstIndex, i + 1).map(pick);
  if (!slice.every(isFiniteNumber)) return null;
  return mean(slice);
}

/**
 * Highest `pick(row)` (total revenue) over the `GROWTH_PEAK_WINDOW` consecutive fiscal years
 * before index `i`. Null when year `i−1` is not consecutive or its value is
 * missing or exactly 0; an exact 0 further back is skipped (FMP's "no
 * figure"). At most 5 rows read: O(1) per year.
 */
export function priorPeak(rows, i, pick) {
  if (!consecutiveYears(rows, i, i - 1)) return null;
  const base = pick(rows[i - 1]);
  // `base === 0` is almost dead code: computeYearGrowthScores' own ≤0-revenue
  // early return already intercepts every case where rows[i].revenue is ALSO
  // finite, which covers every realistic exactly-0 base year. It only still
  // fires when the SCORED year's own revenue is missing (not merely ≤0) while
  // the base year's is exactly 0 — and even then the final score comes out
  // null regardless (a missing revenue nulls revenuePerShare and every growth
  // rate downstream), so this guard changes no externally visible result.
  // Left in rather than deleted: it is this function's own contract (a 0
  // "peak" is nonsensical) and documents it for any other future caller.
  if (!isFiniteNumber(base) || base === 0) return null;
  let peak = base;
  for (let j = 2; j <= GROWTH_PEAK_WINDOW && consecutiveYears(rows, i - j + 1, i - j); j++) {
    const value = pick(rows[i - j]);
    if (isFiniteNumber(value) && value !== 0) peak = Math.max(peak, value);
  }
  return peak;
}

/**
 * Above-peak share of a year's revenue increase (0..1): 1 when the base year
 * is the peak (`base >= peak`), `(revenue − peak) / (revenue − base)` when
 * the year ends above a higher earlier peak, 0 when it ends at or below it,
 * and 0 when base or peak is not positive or revenue is missing. Times total
 * growth this is `max(0, revenue − peak) / base`: only growth to a new high
 * counts. A recovery toward an old peak earns nothing.
 */
export const growthPeakFactor = (revenue, base, peak) => {
  if (!(peak > 0 && base > 0)) return 0;
  if (base >= peak) return 1;
  if (isFiniteNumber(revenue) && revenue > peak) return (revenue - peak) / (revenue - base);
  return 0;
};

/**
 * Share (0..1) of the last `GROWTH_TREND_WINDOW` consecutive YoY changes of
 * `pick(row)` (total revenue) ending at index `i` in which it did not fall.
 * The walk stops at a gap year or a missing or exactly-0 value; null when
 * fewer than `GROWTH_TREND_MIN_CHANGES` changes remain. At most 10 rows read.
 */
export function risingYearShare(rows, i, pick) {
  let rising = 0;
  let changes = 0;
  for (let k = i; k > i - GROWTH_TREND_WINDOW && consecutiveYears(rows, k, k - 1); k--) {
    const prior = pick(rows[k - 1]);
    const current = pick(rows[k]);
    if (!isFiniteNumber(prior) || !isFiniteNumber(current) || prior === 0 || current === 0) break;
    changes++;
    if (current >= prior) rising++;
  }
  return changes >= GROWTH_TREND_MIN_CHANGES ? rising / changes : null;
}

/**
 * Growth-credit multiplier from `risingYearShare`: 0 at or below
 * GROWTH_TREND_ZERO_SHARE, 1 at or above GROWTH_TREND_FULL_SHARE, linear in
 * between; 1 when the share is null (too little history to judge).
 */
export const growthTrendFactor = (share) => (
  share == null
    ? 1
    : clamp((share - GROWTH_TREND_ZERO_SHARE) / (GROWTH_TREND_FULL_SHARE - GROWTH_TREND_ZERO_SHARE), 0, 1)
);

/**
 * One year's Growth score, 0-100 rounded to 2 dp, from its revenue growth
 * `growth`, the growth-credit factor (above-peak share × trend factor) and
 * that year's margin. Null when growth or
 * margin is missing. The per-year arithmetic shared byte-for-byte with
 * funviz-react's qualityScore.js `growthYearScore`.
 */
export function growthYearScore(growth, peakFactor, margin) {
  if (!isFiniteNumber(growth) || !isFiniteNumber(margin) || !isFiniteNumber(peakFactor)) return null;
  const effectiveGrowth = growth > 0 ? Math.min(growth * peakFactor, GROWTH_GROWTH_CAP) : growth;
  const marginTerm = GROWTH_MARGIN_WEIGHT * clamp(margin, GROWTH_MARGIN_FLOOR, GROWTH_MARGIN_CAP);
  const raw = clamp((effectiveGrowth + marginTerm) / GROWTH_FULL_SCORE, 0, 1) * 100;
  // Growth ceiling: below GROWTH_CEILING_GROWTH (15%) the score is capped in
  // proportion (5% -> 33.3, 10% -> 66.7); zero or negative growth caps it at 0.
  let ceiling;
  if (effectiveGrowth <= 0) ceiling = 0;
  else if (effectiveGrowth >= GROWTH_CEILING_GROWTH) ceiling = 100;
  else ceiling = (100 * effectiveGrowth) / GROWTH_CEILING_GROWTH;
  return round(Math.min(raw, ceiling), 2);
}

/**
 * Raw statement rows -> the per-fiscal-year rows every Quality and Growth
 * pillar reads. One row per income statement with a date; cash-flow and
 * balance-sheet rows join by EXACT statement date. Inputs are the raw
 * statement arrays (reporting currency, never FX-converted). `year` is the
 * fiscal year (`fiscalYear`, else the statement date's year).
 */
export function buildQualityRows({ income = [], cashflow = [], balance = [] } = {}) {
  const statements = (income || []).filter((r) => r && typeof r.date === 'string');
  const cashflowByDate = new Map((cashflow || []).map((r) => [r.date, r]));
  const balanceByDate = new Map((balance || []).map((r) => [r.date, r]));

  const fiscalYear = (row) => {
    const n = Number.parseInt(row.fiscalYear ?? row.date.slice(0, 4), 10);
    return Number.isFinite(n) ? n : null;
  };

  return statements.map((row) => {
    const cf = cashflowByDate.get(row.date) || null;
    const bs = balanceByDate.get(row.date) || null;
    const revenue = Number.isFinite(row.revenue) ? row.revenue : null;
    const netIncome = Number.isFinite(row.netIncome) ? row.netIncome : null;
    const shares = isPosFinite(row.weightedAverageShsOut) ? row.weightedAverageShsOut : null;
    const netDebtReportingCcy =
      Number.isFinite(bs?.totalDebt) && Number.isFinite(bs?.cashAndCashEquivalents)
        ? bs.totalDebt - bs.cashAndCashEquivalents
        : null;
    const fcf = Number.isFinite(cf?.freeCashFlow) ? cf.freeCashFlow : null;
    // Raw SBC (signed) — floored inside `ownerEarningsFcf`.
    const sbc = Number.isFinite(cf?.stockBasedCompensation) ? cf.stockBasedCompensation : null;
    return {
      date: row.date,
      year: fiscalYear(row),
      revenue,
      revenuePerShare: isPosFinite(shares) && revenue != null ? revenue / shares : null,
      netIncome,
      // FMP's own raw `ebitda` field — see the param-doc note above for why
      // this is NOT the operatingIncome+D&A proxy used elsewhere in this file.
      ebitda: Number.isFinite(row.ebitda) ? row.ebitda : null,
      // Raw operatingIncome (not yet a margin) — feeds the Change-1 guard in
      // computeYearQuality/guardedProfitabilityMargin above.
      operatingIncome: Number.isFinite(row.operatingIncome) ? row.operatingIncome : null,
      interestIncome: Number.isFinite(row.interestIncome) ? row.interestIncome : null,
      interestExpense: Number.isFinite(row.interestExpense) ? row.interestExpense : null,
      margin: isPosFinite(revenue) && netIncome != null ? netIncome / revenue : null,
      // Ending-balance equity/assets/goodwill (2026-11-01) — feed the sector
      // leverage helpers (bank/insurance/life/other-financial equity-ratio ramps,
      // and the other-financial goodwill-override switch). Unused for a
      // non-financial, non-REIT row (operatingLeverageCoverage never reads
      // them), so they degrade to `null` with no effect there.
      // An exact-0 equity is FMP's "no figure" (RJF FY2026), so missing.
      equity: Number.isFinite(bs?.totalStockholdersEquity) && bs.totalStockholdersEquity !== 0 ? bs.totalStockholdersEquity : null,
      totalAssets: Number.isFinite(bs?.totalAssets) ? bs.totalAssets : null,
      goodwillAndIntangibleAssets: Number.isFinite(bs?.goodwillAndIntangibleAssets) ? bs.goodwillAndIntangibleAssets : null,
      // Reported FCF — the LEVERAGE pillar's basis. `sbc` (raw, signed) feeds
      // `ownerEarningsFcf`, computed on demand inside `computeYearQuality`.
      fcf,
      sbc,
      // Cash-flow statement OCF and D&A (2026-11-01): OCF is the Growth
      // score's REIT margin basis (`ocfMarginFrom`); D&A feeds the REIT
      // leverage EBITDA (`reitLeverageEbitda`). Unused for other rows.
      operatingCashFlow: Number.isFinite(cf?.operatingCashFlow) ? cf.operatingCashFlow : null,
      depreciationAndAmortization: Number.isFinite(cf?.depreciationAndAmortization) ? cf.depreciationAndAmortization : null,
      shares,
      // netDebt pairs with fcf (both reporting currency) in the leverage pillar's coverage ratio.
      netDebt: netDebtReportingCcy,
    };
  });
}

/** Operating ROA = operatingIncome / totalAssets; null for a zero/missing operating income or non-positive total assets. */
export function operatingRoa(operatingIncome, totalAssets) {
  if (!Number.isFinite(operatingIncome) || operatingIncome === 0 || !isPosFinite(totalAssets)) return null;
  return operatingIncome / totalAssets;
}

/** Per-fiscal-year operating ROA, each income row paired with THAT year's own balance-sheet row by exact `date` (never positional). Map<date, fraction|null>. */
export function operatingRoaByDate(incomeAsc, balanceAnnual) {
  const balanceByDate = new Map((balanceAnnual || []).map((r) => [r.date, r]));
  const result = new Map();
  for (const inc of incomeAsc || []) {
    if (!inc || typeof inc.date !== 'string') continue;
    result.set(inc.date, operatingRoa(inc.operatingIncome, balanceByDate.get(inc.date)?.totalAssets));
  }
  return result;
}

/**
 * The three strict Quality headlines for the LATEST row: 3/5/10-year averages
 * of the per-year score (0-100, 2 dp, or null). `ctx` is the per-year context
 * (`operatingRoaByDate`, `isFinancial`, `isReit`, `industry`).
 */
export function qualityHeadlines(rows, ctx = {}) {
  const last = rows.length - 1;
  return {
    qualityScore: averageQualityWindow(rows, last, QUALITY_AVERAGE_WINDOWS.qualityScore, ctx),
    qualityScore5y: averageQualityWindow(rows, last, QUALITY_AVERAGE_WINDOWS.qualityScore5y, ctx),
    qualityScore10y: averageQualityWindow(rows, last, QUALITY_AVERAGE_WINDOWS.qualityScore10y, ctx),
  };
}
